import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Alert, Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { PageWrapper } from '../../styledComponents/wrappers';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import { deriveOrderType, getLpuStagedOrders } from '../../services/fulfillment/lpuApi';
import { LpuOrder } from '../../services/fulfillment/types';
import { ORDER_TYPE_LABELS } from '../../services/fulfillment/constants';
import { FormatDateString } from '../../utils/helper';

export const LPU_PICKUP_LIST_ROUTE = '/retail/lpu';
export const LPU_PICKUP_DETAIL_ROUTE = '/retail/lpu/:orderId/pickup';

const ageDays = (isoDate: string) =>
  Math.floor((Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24));

const AgeCell: React.FC<{ isoDate: string; warnDays: number }> = ({ isoDate, warnDays }) => {
  const days = ageDays(isoDate);
  const warn = days >= warnDays;
  return (
    <Tooltip title={warn ? `Over ${warnDays} days` : ''}>
      <Box
        sx={{
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: 12,
          fontWeight: warn ? 700 : 400,
          bgcolor: warn ? '#fef2f2' : 'transparent',
          color: warn ? '#b91c1c' : 'text.secondary',
          border: warn ? '1px solid #fecaca' : 'none',
        }}
      >
        {days}d
      </Box>
    </Tooltip>
  );
};

const LpuPickupOrdersList: React.FC = () => {
  const history = useHistory();
  const [staged, setStaged] = useState<LpuOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getLpuStagedOrders()
      .then(setStaged)
      .catch(() => setError('Failed to load staged orders.'))
      .finally(() => setLoading(false));
  }, []);

  const sortedStaged = useMemo(
    () =>
      [...staged].sort((a, b) =>
        (a.stagedAt ?? a.createdAt).localeCompare(b.stagedAt ?? b.createdAt),
      ),
    [staged],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      { headerName: 'Order #', field: 'shopifyOrderName', flex: 0.8 },
      {
        headerName: 'Customer',
        field: 'customer',
        flex: 1,
        valueGetter: (params: GridRenderCellParams<LpuOrder>) =>
          `${params.row.customer.firstName} ${params.row.customer.lastName}`,
      },
      {
        headerName: 'Type',
        field: 'orderType',
        flex: 0.7,
        sortable: false,
        valueGetter: (params: GridRenderCellParams<LpuOrder>) =>
          ORDER_TYPE_LABELS[deriveOrderType(params.row.lines.map(l => l.itemType))],
      },
      {
        headerName: 'Items',
        field: 'lineItemCount',
        flex: 0.4,
        type: 'number',
        valueGetter: (params: GridRenderCellParams<LpuOrder>) => params.row.lines.length,
      },
      {
        headerName: 'Staged',
        field: 'stagedAt',
        flex: 0.9,
        valueFormatter: ({ value }) => (value ? FormatDateString(value) : '—'),
      },
      {
        headerName: 'Days waiting',
        field: 'daysWaiting',
        flex: 0.6,
        sortable: false,
        renderCell: (params: GridRenderCellParams<LpuOrder>) =>
          params.row.stagedAt ? (
            <AgeCell isoDate={params.row.stagedAt} warnDays={7} />
          ) : (
            <Typography sx={{ fontSize: 12, color: 'grey.400' }}>—</Typography>
          ),
      },
    ],
    [],
  );

  return (
    <PageWrapper>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Typography variant="h5">Local Pickup Orders</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <StripedDataGrid
          rows={sortedStaged}
          columns={columns}
          getRowId={row => row.shopifyOrderId}
          autoHeight
          onRowClick={(params: GridRowParams) =>
            history.push(`/retail/lpu/${(params.row as LpuOrder).shopifyOrderId}/pickup`)
          }
          sx={{ cursor: 'pointer' }}
          rowHeight={52}
          pageSizeOptions={[10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      )}
    </PageWrapper>
  );
};

export default LpuPickupOrdersList;
